import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import axios from 'axios';
import { Link, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import Modal from '../../components/common/Modal';
import PhoneVerificationModal from '../../components/common/PhoneVerificationModal';
import EmailVerificationModal from '../../components/common/EmailVerificationModal';
import { toast } from 'react-hot-toast';
import ProfileSection from './sections/ProfileSection';
import './profile.css';
import AppearanceSection from './sections/AppearanceSection';
import AccountSection from './sections/AccountSection';
import BillingSection from './sections/BillingSection';
import OrganizationSection from './sections/OrganizationSection';
import OrganizationsManagementSection from './sections/OrganizationsManagementSection';
import IntegrationsSection from './sections/IntegrationsSection';
import MemorySection from './sections/MemorySection';
import { useTheme } from '../../features/theme/context/ThemeContext';
import { useAuthContext } from '../auth/context/AuthContext';
import { clearAuth } from '../../utils/authHelper';
import { canManageOrganization, canAccessOrganizationPages } from '../../utils/planPermissions';
import ProtectedRoute from '../../components/common/ProtectedRoute';
import { getBrandAssetsCached } from '../../services/brandAssets';

// Branded Access Restricted Component
const BrandedAccessRestricted = ({ title, description }) => {
  const { darkMode } = useTheme();
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoLoaded, setLogoLoaded] = useState(false);

  // Improved proxy URL function to better handle S3 URLs
  const getProxiedImageUrl = (s3Url) => {
    if (!s3Url) return null;
    
    // If it's already not an S3 URL, just return it as is
    if (!s3Url.includes('amazonaws.com')) {
      return s3Url;
    }
    
    // Extract file key from the S3 URL (everything after the bucket name)
    const urlParts = s3Url.split('/');
    const fileKey = urlParts.slice(3).join('/');
    
    // Return URL that will be handled by our backend proxy endpoint
    return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
  };

  // Fetch brand assets on component mount and when darkMode changes
  useEffect(() => {
    const fetchBrandAssets = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setLogoLoaded(true);
          return;
        }
        
        // Get brand assets - this checks both user and organization assets
        const { brandAssets } = await getBrandAssetsCached({ token });
        
        // Only use brand assets if processed and enabled
        if (brandAssets?.isProcessed && brandAssets?.isEnabled !== false) {
          // Use brandMarkWordMark (full logo with text) for access restricted screen
          const logo = darkMode ? 
            brandAssets.brandMarkWordMark?.white : 
            brandAssets.brandMarkWordMark?.black;
          
          if (logo) {
            // Always use the proxy for S3 URLs
            const proxyUrl = getProxiedImageUrl(logo);
            setLogoUrl(proxyUrl);
            setLogoLoaded(true);
            return;
          }
        }
        
        // No custom logo available
        setLogoLoaded(true);
      } catch (error) {
        console.error('Error fetching brand assets:', error);
        setLogoLoaded(true);
      }
    };
    
    fetchBrandAssets();
  }, [darkMode]);

  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        {logoUrl && logoLoaded && (
          <div className="mb-6">
            <img 
              src={logoUrl} 
              alt="Company Logo" 
              className="h-8 mx-auto object-contain"
              onError={() => setLogoUrl(null)}
            />
          </div>
        )}
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">{title}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </div>
  );
};

const Profile = ({ user, setUser, setIsAuthenticated, setIsOnboarded }) => {
  const { darkMode } = useTheme();
  const { setAuthTransitioning } = useAuthContext();
  const [isLoading, setIsLoading] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const fileInputRef = useRef(null);
  const currentLocation = useLocation();
  const [userRole, setUserRole] = useState('member');
  const initializationDone = useRef(false);

  // Phone and email verification modal state
  const [showPhoneVerificationModal, setShowPhoneVerificationModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [pendingPhoneNumber, setPendingPhoneNumber] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');

  // Toast style based on dark mode - memoized to prevent re-renders
  const toastStyle = useMemo(() => ({
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
    loading: {
      style: {
        background: darkMode ? '#1e3a8a' : '#eff6ff',
        color: darkMode ? '#fff' : '#1e40af',
        border: `1px solid ${darkMode ? '#1e40af' : '#dbeafe'}`,
      },
    },
  }), [darkMode]);

  // Get user ID once to prevent re-renders
  const userId = user?.id;

  // Initialize user data effect - Only run once per user
  useEffect(() => {
    if (!userId || initializationDone.current) return;
    
    initializationDone.current = true;
    
    // Initialize userRole from localStorage first
    const savedRole = localStorage.getItem('userRole');
    if (savedRole) {
      setUserRole(savedRole);
    }
    
    // Get user role from API
    const fetchUserRole = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/organization/my-role`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        
        const role = data.role || 'member';
        setUserRole(role);
        localStorage.setItem('userRole', role);
      } catch (error) {
        console.error('Error fetching user role:', error);
        setUserRole('member');
      }
    };
    
    fetchUserRole();
  }, [userId]); // Only depend on userId

  // Reset initialization when user changes
  useEffect(() => {
    if (!userId) {
      initializationDone.current = false;
    }
  }, [userId]);

  // Effect to save the current tab to sessionStorage when it changes
  useEffect(() => {
    const path = currentLocation.pathname;
    
    // Extract the tab name from the path
    let tabName = 'profile';
    
    if (path === '/profile' || path === '/settings') {
      tabName = 'profile';
    } else if (path.includes('/settings/')) {
      // Extract the tab name from /settings/X
      const parts = path.split('/');
      if (parts.length >= 3) {
        tabName = parts[2];
      }
    }
    
    // Save the current tab in session storage
    sessionStorage.setItem('settingsActiveTab', tabName);
  }, [currentLocation.pathname]);

  const handleLogoutClick = useCallback(() => {
    setShowLogoutModal(true);
  }, []);

  const handleLogoutConfirm = useCallback(async () => {
    try {
      flushSync(() => setAuthTransitioning(true)); // Force sync re-render to show AuthLoader before clear
      clearAuth(); // Must clear BOTH localStorage AND sessionStorage - otherwise token persists and auth route redirects to /
      localStorage.clear(); // Clear rest of localStorage
      window.location.replace('/auth/onboarding');
    } catch (error) {
      console.error('Error during logout:', error);
      window.location.replace('/auth/onboarding');
    }
  }, [setAuthTransitioning]);



  // Phone verification confirm handler
  const handlePhoneVerificationConfirm = useCallback(async (verificationData) => {
    try {
      setIsLoading(true);
      toast.loading('Updating phone number...', { id: 'phoneUpdate', ...toastStyle });

      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update-phone`,
        { 
          phoneNumber: pendingPhoneNumber
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update was successful
      const updatedUser = { ...user, phoneNumber: pendingPhoneNumber };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      // Close modal and reset pending state
      setShowPhoneVerificationModal(false);
      setPendingPhoneNumber('');
      
      toast.success('Phone number updated successfully', { id: 'phoneUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating phone number:', error);
      const errorMessage = error.response?.data?.error || 'Failed to update phone number. Please try again.';
      toast.error(errorMessage, { id: 'phoneUpdate', ...toastStyle.error });
    } finally {
      setIsLoading(false);
    }
  }, [pendingPhoneNumber, user, setUser, toastStyle]);

  // Email verification confirm handler
  const handleEmailVerificationConfirm = useCallback(async (verificationData) => {
    try {
      setIsLoading(true);
      toast.loading('Updating email address...', { id: 'emailUpdate', ...toastStyle });

      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/users/update`,
        { 
          email: pendingEmail
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Update was successful
      const updatedUser = { ...user, email: pendingEmail };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      
      // Close modal and reset pending state
      setShowEmailVerificationModal(false);
      setPendingEmail('');
      
      toast.success('Email address updated successfully', { id: 'emailUpdate', ...toastStyle.success });
    } catch (error) {
      console.error('Error updating email address:', error);
      toast.error('Failed to update email address. Please try again.', { id: 'emailUpdate', ...toastStyle.error });
    } finally {
      setIsLoading(false);
    }
  }, [pendingEmail, user, setUser, toastStyle]);

  // Add this to silence Stripe telemetry errors
  if (window.Stripe) {
    window.Stripe.enableTelemetry = false;
  }

  // Add this to silence Amplitude errors
  if (window.amplitude) {
    window.amplitude.options.logLevel = 'NONE';
  }

  const isActive = useCallback((tab) => {
    // For the profile tab, check both /profile and /settings (root)
    if (tab === 'profile') {
      return currentLocation.pathname === '/profile' || currentLocation.pathname === '/settings';
    }
    
    // For other tabs, check if the pathname contains their route segment
    // This handles both direct matches and nested routes
    if (currentLocation.pathname.includes(`/settings/${tab}`)) {
      return true;
    }
    
    return false;
  }, [currentLocation.pathname]);



  // Get the current organization sub-tab
  const getOrganizationSubTab = useCallback(() => {
    const path = currentLocation.pathname;
    
    // Only return active tab if we're actually in the organization section
    if (!path.includes('/settings/organization')) {
      return null; // No active tab when not in organization section
    }
    
    if (path === '/settings/organization' || path === '/settings/organization/') {
      return 'members'; // Default to members
    }
    if (path.includes('/settings/organization/members')) return 'members';
    if (path.includes('/settings/organization/context')) return 'context';
    if (path.includes('/settings/organization/usage')) return 'usage';
    if (path.includes('/settings/organization/preferences')) return 'preferences';
    return 'members'; // Default fallback within organization section
  }, [currentLocation.pathname]);

  return (
    <div className="profile-root flex h-screen pt-10 bg-white dark:bg-[#111111]">
      {/* Mobile Navigation - Outside desktop constraints */}
      <div className="md:hidden fixed top-10 left-0 right-0 bg-white dark:bg-black z-10 border-b border-gray-200 dark:border-gray-800">
        <div className="pb-4">
          <h1 className="text-2xl font-semibold text-black dark:text-white mb-4 px-4 pt-4">Settings</h1>
          <div className="overflow-x-auto">
            <div className="flex gap-2 px-4 pb-2" style={{ width: 'max-content' }}>
              <Link
                to="/profile"
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                  isActive('profile')
                    ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                }`}
              >
                Profile
              </Link>
              <Link
                to="/settings/appearance"
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                  isActive('appearance')
                    ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                }`}
              >
                Appearance
              </Link>
              {userRole !== 'member' && (
                <>
                  <Link
                    to="/settings/billing"
                    className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                      isActive('billing')
                        ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                        : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                    }`}
                  >
                    Billing
                  </Link>
                  {canAccessOrganizationPages(user) && (
                    <Link
                      to="/settings/organization"
                      className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                        isActive('organization')
                          ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                          : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                      }`}
                    >
                      Organization
                    </Link>
                  )}
                  
                  {/* Organization Submenu for Mobile - Always show when user has access */}
                  {canAccessOrganizationPages(user) && (
                    <>
                      <Link
                        to="/settings/organization/members"
                        className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm ${
                          getOrganizationSubTab() === 'members'
                            ? 'bg-gray-200 text-black dark:bg-gray-700 dark:text-white'
                            : 'text-gray-500 bg-gray-100 dark:bg-[#1A1A1A] dark:text-gray-500'
                        }`}
                      >
                        Members
                      </Link>
                      <Link
                        to="/settings/organization/context"
                        className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm ${
                          getOrganizationSubTab() === 'context'
                            ? 'bg-gray-200 text-black dark:bg-gray-700 dark:text-white'
                            : 'text-gray-500 bg-gray-100 dark:bg-[#1A1A1A] dark:text-gray-500'
                        }`}
                      >
                        Context
                      </Link>
                      <Link
                        to="/settings/organization/usage"
                        className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm ${
                          getOrganizationSubTab() === 'usage'
                            ? 'bg-gray-200 text-black dark:bg-gray-700 dark:text-white'
                            : 'text-gray-500 bg-gray-100 dark:bg-[#1A1A1A] dark:text-gray-500'
                        }`}
                      >
                        Usage
                      </Link>
                      {canManageOrganization(user, userRole) && (
                        <Link
                          to="/settings/organization/preferences"
                          className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg text-sm ${
                            getOrganizationSubTab() === 'preferences'
                              ? 'bg-gray-200 text-black dark:bg-gray-700 dark:text-white'
                              : 'text-gray-500 bg-gray-100 dark:bg-[#1A1A1A] dark:text-gray-500'
                          }`}
                        >
                          Preferences
                        </Link>
                      )}
                    </>
                  )}
                  
                </>
              )}
              {canManageOrganization(user, userRole) && (
                <Link
                  to="/settings/memory"
                  className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                    isActive('memory')
                      ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                      : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                  }`}
                >
                  Memory
                </Link>
              )}
              <Link
                to="/settings/integrations"
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                  isActive('integrations')
                    ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                }`}
              >
                Integrations
              </Link>
              <div className="flex-shrink-0 w-px h-8 bg-[#444444] mx-2"></div>
              <Link
                to="/settings/organizations"
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                  isActive('organizations')
                    ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                }`}
              >
                My Organizations
              </Link>
              <Link
                to="/settings/account"
                className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-lg ${
                  isActive('account')
                    ? 'bg-gray-100 text-black dark:bg-gray-800 dark:text-white'
                    : 'text-gray-600 bg-gray-50 dark:bg-[#181818] dark:text-gray-400'
                }`}
              >
                Settings
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main centered container */}
      <div className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full mt-0 md:mt-0" style={{ marginTop: window.innerWidth < 768 ? '120px' : '0' }}>
          {/* Desktop and Mobile Content Container */}
          <div className="flex relative">
            {/* Settings Sidebar - Hidden on mobile */}
            <div className="profile-settings-sidebar hidden md:block w-64 flex-shrink-0">
              <div className="sticky top-10">
                <div className="profile-settings-sidebar-inner p-6">
                  <div className="profile-settings-header">
                    <h1 className="profile-settings-title text-2xl font-semibold text-black dark:text-white px-3">Account</h1>
                  </div>
                  <div className="profile-settings-nav-scroll">
                    <nav className="profile-settings-nav mt-6">
                    <Link
                      to="/profile"
                      className={`block px-3 py-2 mb-1 rounded-lg ${
                        isActive('profile')
                          ? 'bg-gray-100 text-black dark:bg-[#181818] dark:text-white'
                          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                      }`}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/settings/appearance"
                      className={`block px-3 py-2 mb-1 rounded-lg ${
                        isActive('appearance')
                          ? 'bg-gray-100 dark:bg-[#181818] text-black dark:text-white'
                          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                      }`}
                    >
                      Appearance
                    </Link>
                    {userRole !== 'member' && (
                      <>
                        <Link
                          to="/settings/billing"
                          className={`block px-3 py-2 mb-1 rounded-lg ${
                            isActive('billing')
                              ? 'bg-gray-100 text-gray-900 dark:bg-[#181818] dark:text-white'
                              : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                          }`}
                        >
                          Billing
                        </Link>
                        {canAccessOrganizationPages(user) && (
                          <Link
                            to="/settings/organization"
                            className={`block px-3 py-2 mb-2 rounded-lg ${
                              isActive('organization')
                                ? 'bg-gray-100 text-gray-900 dark:bg-[#181818] dark:text-white'
                                : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                            }`}
                          >
                            Organization
                          </Link>
                        )}
                        
                        {/* Organization Submenu - Always show when user has access */}
                        {canAccessOrganizationPages(user) && (
                          <div className="profile-settings-subnav ml-4 space-y-1 mb-1.5 relative">
                            {/* Vertical connecting line */}
                            <div className="absolute left-0 top-0 bottom-0 w-px mt-2.5 mb-2.5 bg-gray-300 dark:bg-gray-600"></div>
                            <Link
                              to="/settings/organization/members"
                              className={`block px-3 py-2 rounded-lg text-[15px] ${
                                getOrganizationSubTab() === 'members'
                                  ? 'text-gray-900 dark:text-white'
                                  : 'text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              Members
                            </Link>
                            <Link
                              to="/settings/organization/context"
                              className={`block px-3 py-2 rounded-lg text-[15px] ${
                                getOrganizationSubTab() === 'context'
                                  ? 'text-gray-900 dark:text-white'
                                  : 'text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              Context
                            </Link>
                            <Link
                              to="/settings/organization/usage"
                              className={`block px-3 py-2 rounded-lg text-[15px] ${
                                getOrganizationSubTab() === 'usage'
                                  ? 'text-gray-900 dark:text-white'
                                  : 'text-gray-600 dark:text-gray-400'
                              }`}
                            >
                              Usage
                            </Link>
                            {canManageOrganization(user, userRole) && (
                              <Link
                                to="/settings/organization/preferences"
                                className={`block px-3 py-2 rounded-lg text-[15px] ${
                                  getOrganizationSubTab() === 'preferences'
                                    ? 'text-gray-900 dark:text-white'
                                    : 'text-gray-600 dark:text-gray-400'
                                }`}
                              >
                                Preferences
                              </Link>
                            )}
                          </div>
                        )}
                        
                      </>
                    )}
                    {canManageOrganization(user, userRole) && (
                      <Link
                        to="/settings/memory"
                        className={`block px-3 py-2 mb-1 rounded-lg ${
                          isActive('memory')
                            ? 'bg-gray-100 text-gray-900 dark:bg-[#181818] dark:text-white'
                            : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                        }`}
                      >
                        Memory
                      </Link>
                    )}
                    <Link
                      to="/settings/integrations"
                      className={`block px-3 py-2 mb-1 rounded-lg ${
                        isActive('integrations')
                          ? 'bg-gray-100 text-gray-900 dark:bg-[#181818] dark:text-white'
                          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                      }`}
                    >
                      Integrations
                    </Link>
                    <div className="h-px bg-[#e9e9e9] dark:bg-[#2a2a2a] mx-3 my-3"></div>
                    <Link
                      to="/settings/organizations"
                      className={`block px-3 py-2 mb-1 rounded-lg ${
                        isActive('organizations')
                          ? 'bg-gray-100 text-gray-900 dark:bg-[#181818] dark:text-white'
                          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                      }`}
                    >
                      My Organizations
                    </Link>
                    <Link
                      to="/settings/account"
                      className={`block px-3 py-2 mb-1 rounded-lg ${
                        isActive('account')
                          ? 'bg-gray-100 text-gray-900 dark:bg-[#181818] dark:text-white'
                          : 'text-gray-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:bg-[#181818]'
                      }`}
                    >
                      Settings
                    </Link>
                    </nav>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 pt-5 min-w-0 pl-0 md:pl-8">
              <div className="h-full">
                <Routes>
                  <Route 
                    path="/"
                    element={
                      <ProfileSection 
                        user={user}
                        fileInputRef={fileInputRef}
                        setUser={setUser}
                        userRole={userRole}
                        // Phone verification modal props
                        showPhoneVerificationModal={showPhoneVerificationModal}
                        setShowPhoneVerificationModal={setShowPhoneVerificationModal}
                        pendingPhoneNumber={pendingPhoneNumber}
                        setPendingPhoneNumber={setPendingPhoneNumber}
                        onPhoneVerificationConfirm={handlePhoneVerificationConfirm}
                        // Email verification modal props
                        showEmailVerificationModal={showEmailVerificationModal}
                        setShowEmailVerificationModal={setShowEmailVerificationModal}
                        pendingEmail={pendingEmail}
                        setPendingEmail={setPendingEmail}
                        onEmailVerificationConfirm={handleEmailVerificationConfirm}
                      />
                    } 
                  />
                  <Route 
                    path="appearance" 
                    element={<AppearanceSection />} 
                  />
                  <Route 
                    path="account" 
                    element={
                      <AccountSection 
                        handleLogoutClick={handleLogoutClick}
                        user={user}
                      />
                    } 
                  />
                  <Route 
                    path="billing" 
                    element={
                      <ProtectedRoute 
                        canAccess={canManageOrganization(user, userRole)}
                        redirectTo="/profile"
                        fallback={
                          <BrandedAccessRestricted 
                            title="Access Restricted"
                            description="Only organization administrators and owners can access billing information."
                          />
                        }
                      >
                        <BillingSection />
                      </ProtectedRoute>
                    } 
                  />
                  {canAccessOrganizationPages(user) && (
                    <Route 
                      path="organization" 
                      element={<Navigate to="/settings/organization/members" replace />} 
                    />
                  )}
                  {canAccessOrganizationPages(user) && (
                    <>
                      <Route 
                        path="organization/members" 
                        element={
                          <ProtectedRoute 
                            canAccess={canManageOrganization(user, userRole)}
                            redirectTo="/profile"
                                                         fallback={
                               <BrandedAccessRestricted 
                                 title="Access Restricted"
                                 description="Only organization administrators and owners can access member management."
                               />
                             }
                          >
                            <OrganizationSection user={user} activeTab="members" />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="organization/context" 
                        element={
                          <ProtectedRoute 
                            canAccess={canManageOrganization(user, userRole)}
                            redirectTo="/profile"
                                                         fallback={
                               <BrandedAccessRestricted 
                                 title="Access Restricted"
                                 description="Only organization administrators and owners can access context settings."
                               />
                             }
                          >
                            <OrganizationSection user={user} activeTab="context" />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="organization/usage" 
                        element={
                          <ProtectedRoute 
                            canAccess={canManageOrganization(user, userRole)}
                            redirectTo="/profile"
                                                         fallback={
                               <BrandedAccessRestricted 
                                 title="Access Restricted"
                                 description="Only organization administrators and owners can access usage information."
                               />
                             }
                          >
                            <OrganizationSection user={user} activeTab="usage" />
                          </ProtectedRoute>
                        } 
                      />
                      <Route 
                        path="organization/preferences" 
                        element={
                          <ProtectedRoute 
                            canAccess={canManageOrganization(user, userRole)}
                            redirectTo="/profile"
                            fallback={
                              <BrandedAccessRestricted 
                                title="Access Restricted"
                                description="Only organization administrators and owners can access preferences settings."
                              />
                            }
                          >
                            <OrganizationSection user={user} activeTab="preferences" />
                          </ProtectedRoute>
                        } 
                      />
                    </>
                  )}
                  {canManageOrganization(user, userRole) && (
                    <Route 
                      path="memory" 
                      element={
                        <ProtectedRoute 
                          canAccess={canManageOrganization(user, userRole)}
                          redirectTo="/profile"
                          fallback={
                            <BrandedAccessRestricted 
                              title="Access Restricted"
                              description="Only organization administrators and owners can access memory management."
                            />
                          }
                        >
                          <MemorySection />
                        </ProtectedRoute>
                      } 
                    />
                  )}
                  <Route 
                    path="integrations" 
                    element={<IntegrationsSection user={user} />} 
                  />
                  <Route 
                    path="organizations" 
                    element={<OrganizationsManagementSection user={user} setUser={setUser} />} 
                  />
                  <Route
                    path="*"
                    element={<Navigate to="/profile" replace />}
                  />
                </Routes>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      <Modal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={handleLogoutConfirm}
        title="Confirm Logout"
        message="Are you sure you want to log out? You will need to sign in again to access your account."
        confirmText="Log Out"
        cancelText="Stay Signed In"
      />

      {/* Phone Verification Modal */}
      <PhoneVerificationModal
        isOpen={showPhoneVerificationModal}
        onClose={() => {
          setShowPhoneVerificationModal(false);
          setPendingPhoneNumber('');
        }}
        onConfirm={handlePhoneVerificationConfirm}
        phoneNumber={pendingPhoneNumber}
        title="Verify New Phone Number"
      />

      {/* Email Verification Modal */}
      <EmailVerificationModal
        isOpen={showEmailVerificationModal}
        onClose={() => {
          setShowEmailVerificationModal(false);
          setPendingEmail('');
        }}
        onConfirm={handleEmailVerificationConfirm}
        email={pendingEmail}
        title="Verify New Email Address"
      />
    </div>
  );
};

export default Profile; 